// Shared Ready-to-Teach rule. Mirrored in the silver-sage-assistant edge function.
// Returns a structured list of blockers so UI + assistant can render the same
// language everywhere. Never mutates records; the admin override is stored
// separately on the instructor row and does NOT clear the underlying blockers.
//
// Canonical Teaching Agreement / Background Check status:
//   A requirement counts as CURRENT when the completion flag is true AND
//   either the `_year` column OR the year parsed from the `_signed_date` /
//   `_date` column is >= the current year. This tolerates records where an
//   admin set the date but never entered the year explicitly.

export type ReadinessInstructor = {
  id: string;
  status?: string | null;
  phone: string | null;
  email: string | null;
  pay_rate_type: string | null;
  pay_rate_amount: number | null;
  hourly_rate: number | null;
  payment_method: string | null;
  zelle_contact?: string | null;
  zelle_name?: string | null;
  // Legacy — Independent Contractor acknowledgement is NO LONGER a
  // readiness requirement. The Teaching Agreement is the governing
  // document. Field kept for historical DB rows only; do not read.
  independent_contractor_ack?: boolean | null;
  w9_status: string | null;
  teaching_agreement_signed: boolean | null;
  teaching_agreement_year: number | null;
  teaching_agreement_signed_date?: string | null;
  background_check_completed: boolean | null;
  background_check_year: number | null;
  background_check_date?: string | null;
  // Legacy — Standards of Conduct is covered by the Teaching Agreement.
  standards_of_conduct_acknowledged?: boolean | null;
  onboarding_status: string | null;
  ready_to_teach_override?: boolean | null;
  ready_to_teach_override_reason?: string | null;
  ready_to_teach_override_by?: string | null;
  ready_to_teach_override_at?: string | null;
};

export type ReadinessBlocker = {
  key: string;
  label: string;
  jumpTo: string; // dialog section anchor used by "Jump to" quick links
};

export type ReadinessResult = {
  ready: boolean;
  overridden: boolean;
  status: "ready" | "override" | "not_ready";
  statusLabel: "Ready to Teach" | "Ready with Admin Override" | "Not Ready to Teach";
  blockers: ReadinessBlocker[];
  overrideReason?: string | null;
  overrideBy?: string | null;
  overrideAt?: string | null;
};

function yearFromDate(date: string | null | undefined): number | null {
  if (!date) return null;
  const m = String(date).match(/^(\d{4})/);
  if (!m) return null;
  const y = Number(m[1]);
  return Number.isFinite(y) ? y : null;
}

/** Canonical: does the instructor have a current Teaching Agreement on file? */
export function hasTeachingAgreement(i: ReadinessInstructor, currentYear?: number): boolean {
  if (!i.teaching_agreement_signed) return false;
  const year = currentYear ?? new Date().getFullYear();
  const y = i.teaching_agreement_year ?? yearFromDate(i.teaching_agreement_signed_date);
  // If neither year nor date is present but the signed flag is true, honour
  // the flag — the signed toggle is the admin's explicit confirmation.
  if (y == null) return true;
  return y >= year;
}

/** Canonical: does the instructor have a current background check on file? */
export function hasCurrentBackgroundCheck(i: ReadinessInstructor, currentYear?: number): boolean {
  if (!i.background_check_completed) return false;
  const year = currentYear ?? new Date().getFullYear();
  const y = i.background_check_year ?? yearFromDate(i.background_check_date);
  if (y == null) return true;
  return y >= year;
}

/**
 * Payment information is COMPLETE when the instructor has any of:
 *   - a valid Zelle email or phone number stored on `zelle_contact`
 *   - a configured non-Zelle payment method (e.g., "Cash" for Coach C)
 *
 * If `payment_method` is "Zelle" the Zelle contact is required.
 * If `payment_method` is any other non-empty value it is considered an
 * approved payment method on its own.
 */
export function hasPaymentInformation(i: ReadinessInstructor): boolean {
  const method = (i.payment_method ?? "").trim();
  const zelle = (i.zelle_contact ?? "").trim();
  if (!method && !zelle) return false;
  if (method.toLowerCase().includes("zelle")) return zelle.length > 0;
  if (method.length > 0) return true;
  // No explicit method configured — a stored Zelle contact still counts.
  return zelle.length > 0;
}

export function checkInstructorReadiness(
  i: ReadinessInstructor,
  opts: { hasAssignment: boolean; currentYear?: number },
): ReadinessResult {
  const year = opts.currentYear ?? new Date().getFullYear();
  const isVolunteer = i.status === "volunteer";
  const b: ReadinessBlocker[] = [];

  // Simplified Ready-to-Teach checklist:
  //   Volunteer      → Teaching Agreement + Background Check
  //   Paid (active)  → Teaching Agreement + Background Check + W-9 + Payment info
  // Standards of Conduct is covered by the Teaching Agreement and is no longer
  // tracked as a separate requirement. Historical SoC fields are preserved on
  // the instructor record but do not block readiness.
  if (!hasTeachingAgreement(i, year)) {
    b.push({ key: "teaching_agreement", label: `${year} teaching agreement`, jumpTo: "compliance" });
  }
  if (!hasCurrentBackgroundCheck(i, year)) {
    b.push({ key: "background_check", label: `${year} background check`, jumpTo: "compliance" });
  }
  if (!isVolunteer) {
    const w9Ok = i.w9_status === "Received" || i.w9_status === "Exempt or Not Required";
    if (!w9Ok) {
      b.push({ key: "w9", label: "W-9 received", jumpTo: "contractor" });
    }
    if (!hasPaymentInformation(i)) {
      b.push({ key: "payment_method", label: "Payment information", jumpTo: "pay" });
    }
  }

  const ready = b.length === 0;
  const overridden = !!i.ready_to_teach_override;
  const status: ReadinessResult["status"] = ready ? "ready" : overridden ? "override" : "not_ready";
  const statusLabel = status === "ready"
    ? "Ready to Teach"
    : status === "override"
    ? "Ready with Admin Override"
    : "Not Ready to Teach";

  return {
    ready,
    overridden,
    status,
    statusLabel,
    blockers: b,
    overrideReason: i.ready_to_teach_override_reason ?? null,
    overrideBy: i.ready_to_teach_override_by ?? null,
    overrideAt: i.ready_to_teach_override_at ?? null,
  };
}
